import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { UserPlus, ArrowRight, Loader2 } from 'lucide-react';

interface SessionAssignment {
  id: string;
  summary: string | null;
  decision_type: string | null;
  phase: number;
  role: string;
}

interface NewAssignmentsDialogProps {
  userId: string;
  workspaceId: string;
}

export const NewAssignmentsDialog = ({ userId, workspaceId }: NewAssignmentsDialogProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [assignments, setAssignments] = useState<SessionAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkNewAssignments() {
      const storageKey = `assignments-seen-${userId}-${workspaceId}`;
      const lastSeen = localStorage.getItem(storageKey);

      try {
        let query = supabase
          .from('decision_participants')
          .select(`
            role,
            created_at,
            session_id,
            decision_sessions!inner (
              id,
              summary,
              decision_type,
              phase,
              workspace_id
            )
          `)
          .eq('user_id', userId)
          .neq('added_by', userId);

        // Filter by workspace via the join
        // We'll filter client-side since we can't easily filter nested fields

        if (lastSeen) {
          query = query.gt('created_at', lastSeen);
        }

        const { data, error } = await query.order('created_at', { ascending: false });

        if (error) {
          console.error('Failed to check assignments:', error);
          setLoading(false);
          return;
        }

        // Filter to only this workspace's sessions
        const filtered = (data || []).filter((d: any) => {
          const session = d.decision_sessions;
          return session && session.workspace_id === workspaceId;
        }).map((d: any) => ({
          id: d.decision_sessions.id,
          summary: d.decision_sessions.summary,
          decision_type: d.decision_sessions.decision_type,
          phase: d.decision_sessions.phase,
          role: d.role,
        }));

        if (filtered.length > 0) {
          setAssignments(filtered);
          setOpen(true);
        }
      } catch (err) {
        console.error('Error checking assignments:', err);
      } finally {
        setLoading(false);
      }
    }

    checkNewAssignments();
  }, [userId, workspaceId]);

  const handleClose = () => {
    setOpen(false);
    localStorage.setItem(
      `assignments-seen-${userId}-${workspaceId}`,
      new Date().toISOString()
    );
  };

  const roleLabel = (role: string) => {
    switch (role) {
      case 'owner': return 'Owner';
      case 'contributor': return 'Contributor';
      case 'reviewer': return 'Reviewer';
      case 'viewer': return 'Viewer';
      default: return role;
    }
  };

  const phaseLabel = (phase: number) => {
    switch (phase) {
      case 1: return 'Analyse';
      case 2: return 'Optionen';
      case 3: return 'Abschluss';
      default: return `Phase ${phase}`;
    }
  };

  if (loading || !open || assignments.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-lg border-border/50 bg-card">
        <DialogHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
              <UserPlus className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl font-serif font-semibold">
                Du wurdest hinzugefügt
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-0.5">
                {assignments.length === 1
                  ? 'Du wurdest zu einer Entscheidung eingeladen'
                  : `Du wurdest zu ${assignments.length} Entscheidungen eingeladen`}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-2 py-3 max-h-[50vh] overflow-y-auto">
          {assignments.map((a) => (
            <div
              key={a.id}
              className="group flex items-center justify-between p-3 rounded-xl border border-border/50 bg-background hover:border-primary/20 hover:bg-primary/5 transition-all cursor-pointer"
              onClick={() => {
                handleClose();
                const phasePath = a.phase === 1 ? '' : a.phase === 2 ? '/phase2' : a.phase === 3 ? '/phase3' : '';
                navigate(`/app/session/${a.id}${phasePath}`);
              }}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  {a.summary || a.decision_type || 'Neue Entscheidung'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-xs">
                    {phaseLabel(a.phase)}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    als {roleLabel(a.role)}
                  </span>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 ml-2" />
            </div>
          ))}
        </div>

        <Button onClick={handleClose} variant="outline" className="w-full">
          Verstanden
        </Button>
      </DialogContent>
    </Dialog>
  );
};
